// Generated by Claude Code
/**
 * Ships diagnostics events to OpenObserve through the SWA managed relay
 * (`/api/telemetry/frontend`), which forwards them to the backend. Best-effort:
 * every failure is swallowed, telemetry never surfaces to the page.
 */

const ENDPOINT = '/api/telemetry/frontend'
const MAX_BATCH = 50
const MAX_BODY = 60 * 1024
const MAX_QUEUE = 500
const FLUSH_INTERVAL_MS = 15_000

/** One event in the wire payload; field names and caps are the backend's contract. */
export interface TelemetryEvent {
  kind: 'navigation' | 'error' | 'unhandledrejection'
  ts: string
  url: string
  route_id: string | null
  nav_source: string | null
  message: string | null
  stack: string | null
}

export interface TelemetryIdentity {
  userId?: string | null
  companyId?: string | null
  role?: string | null
}

let identity: TelemetryIdentity = {}
let session: string | null = null
let queue: TelemetryEvent[] = []
let disabled = false
let listenersBound = false
let flushTimer: ReturnType<typeof setTimeout> | undefined

/** Stamps who the events belong to. Called from app bootstrap; the sender never reads auth state. */
export function setTelemetryIdentity(next: TelemetryIdentity): void {
  identity = next
}

function cap(value: string | null | undefined, max: number): string | null {
  if (value == null) return null
  return value.length > max ? value.slice(0, max) : value
}

function readRelease(): string | null {
  return cap(import.meta.env.VITE_BUILD_HASH, 64)
}

function readViewport(): string | null {
  if (typeof window === 'undefined' || typeof window.innerWidth !== 'number') return null
  return `${window.innerWidth}x${window.innerHeight}`
}

function serialize(events: TelemetryEvent[]): string {
  return JSON.stringify({
    release: readRelease(),
    session_id: cap(session, 32),
    user_id: cap(identity.userId, 64),
    company_id: cap(identity.companyId, 64),
    role: cap(identity.role, 128),
    viewport: readViewport(),
    events,
  })
}

function buildBody(events: TelemetryEvent[]): string {
  const batch = events.map((event) => ({ ...event }))
  let body = serialize(batch)
  while (body.length > MAX_BODY) {
    const oldestWithStack = batch.find((event) => event.stack != null)
    if (oldestWithStack) oldestWithStack.stack = null
    else if (batch.length > 1) batch.shift()
    else break
    body = serialize(batch)
  }
  return body
}

function send(body: string): void {
  void (async () => {
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body,
      })
      // No managed API on this origin (vite dev, Storybook) — stop trying for this page.
      if (response.status === 404 || response.status === 405) disabled = true
    } catch {
      try {
        navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }))
      } catch {
        // ignore — best-effort
      }
    }
  })()
}

/** Sends everything queued right now, in batches of at most 50 events. */
export function flushTelemetry(): void {
  if (flushTimer != null) {
    clearTimeout(flushTimer)
    flushTimer = undefined
  }
  if (disabled) {
    queue = []
    return
  }
  while (queue.length > 0) {
    send(buildBody(queue.splice(0, MAX_BATCH)))
  }
}

function addListener(target: EventTarget | undefined, type: string, handler: () => void): void {
  if (typeof target?.addEventListener === 'function') target.addEventListener(type, handler)
}

function bindLifecycleListeners(): void {
  if (listenersBound) return
  listenersBound = true
  addListener(typeof document === 'undefined' ? undefined : document, 'visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTelemetry()
  })
  addListener(typeof window === 'undefined' ? undefined : window, 'pagehide', flushTelemetry)
}

/**
 * Queues one event for sending. Errors go out immediately; navigations ride the
 * next batch, flush interval, or page-hide.
 *
 * @param sessionId - The diagnostics tab id, stamped on the payload that carries the event.
 */
export function enqueueTelemetryEvent(event: TelemetryEvent, sessionId: string): void {
  if (disabled) return
  session = sessionId
  bindLifecycleListeners()
  queue.push(event)
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE)
  if (event.kind !== 'navigation') {
    flushTelemetry()
    return
  }
  flushTimer ??= setTimeout(flushTelemetry, FLUSH_INTERVAL_MS)
}
